import React, { FC, forwardRef, useEffect, useState } from 'react';
import '@adyen/adyen-web/dist/adyen.css';

import {
  useCheckPaymentMutation,
  useGetPaymentMethodsQuery,
  usePostPaymentMutation,
} from '@shared/services/users';
import { useRouter } from 'next/router';
import { mapLocaleCodeToCountry } from '@features/WordPress/utilities/helpers/locale';
import { useDispatch } from 'react-redux';
import { orderCompleted } from '@features/Checkout/store';
import { captureException } from '@sentry/nextjs';
import { membershipApi } from '@features/Membership/services/memberships';
import { useAppSelector } from '@shared/store';
import { useTranslation } from 'next-i18next';
import { Snackbar, Alert as MuiAlert, Slide } from '@mui/material';

interface AdyenDropinProps {
  type?: number;
  sessionId: string;
  isDunning?: boolean;
  setCompleted?: (completed: boolean) => void;
  amount?: number;
  serviceUnitUid?: string;
  isNewCheckout?: boolean;
  skipMembershipValidation?: boolean;
  campaignMembershipIds?: string[];
  campaignId?: string;
  onError?: (error: boolean) => void;
}

const AdyenDropin: FC<AdyenDropinProps> = ({
  type = 1,
  sessionId,
  isDunning = false,
  setCompleted,
  amount = 0,
  serviceUnitUid = '',
  isNewCheckout,
  skipMembershipValidation,
  campaignMembershipIds,
  campaignId,
  onError = () => {},
}) => {
  const { locale, push } = useRouter();
  const dispatch = useDispatch();
  const country = mapLocaleCodeToCountry(locale);
  const { t } = useTranslation(['account']);
  const { membershipIds, terminalSession } = useAppSelector(
    (state) => state.checkout
  );
  const { data: { data: paymentMethodsResponse, meta } = {} } =
    useGetPaymentMethodsQuery();
  const [postPayment] = usePostPaymentMutation();
  const [checkPayment] = useCheckPaymentMutation();

  const [error, setError] = useState<string | null>(null);
  const [isSnackbarOpen, setisSnackbarOpen] = useState(true);

  const Alert = forwardRef((props, ref) => (
    <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
  ));

  const handleAlertClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setisSnackbarOpen(false);
    setError(null);
  };

  const handlePaymentAccepted = (data, { elementRef: dropin }) => {
    if (data.action) {
      dropin.handleAction(data.action);
    } else if (data.orderId) {
      dispatch(orderCompleted(data.orderId));
      dispatch(membershipApi.util.invalidateTags(['Membership']));

      isNewCheckout
        ? push({
            pathname: '/checkout2/confirmation/',
            query: { orderId: data.orderId, newOrder: !isDunning },
          })
        : push({
            pathname: '/checkout/confirmation/',
            query: {
              orderId: data.orderId,
              newOrder: !isDunning,
              ...(campaignId && {
                campaignId: campaignId,
              }),
            },
          });
    } else {
      // update card because no order id
      if (setCompleted) {
        setCompleted(true);
      }
    }
  };

  useEffect(() => {
    if (!paymentMethodsResponse || !sessionId) return;

    // Button Based On Type
    let dkButton = 'Betal';
    let seButton = 'Betala';
    let noButton = 'Betale';
    let deButton = 'Zahlen';

    if (type === 1 && campaignId) {
      dkButton = 'Tilføj kortoplysninger';
    } else if (type === 2) {
      dkButton = 'Opdater';
      seButton = 'Uppdatera';
      noButton = 'Oppdater';
      deButton = 'Aktualisieren';
    } else if (type === 3) {
      dkButton = 'Tilføj betalingskort';
      seButton = 'Lägg till betalkort';
      noButton = 'Legg til betalingskort';
      deButton = 'Zahlungskarte hinzufügen';
    }

    const translations = {
      'sv-SE': {
        payButton: seButton,
      },
      'da-DK': {
        payButton: dkButton,
        confirmPreauthorization: dkButton,
      },
      'no-NO': {
        payButton: noButton,
      },
      'de-DE': {
        payButton: deButton,
      },
    };

    // Locale String
    let localeString = 'da-DK';
    let currency = 'DKK';

    if (country === 'no') {
      localeString = 'no-NO';
      currency = 'NOK';
    } else if (country === 'se') {
      localeString = 'sv-SE';
      currency = 'SEK';
    } else if (country === 'de') {
      localeString = 'de-DE';
      currency = 'EUR';
    }

    // Card Data Local
    let cardDataLocal = {
      name: 'BETALINGSKORT',
    };

    if (country === 'no') {
      cardDataLocal = {
        name: 'BETALINGSKORT',
      };
    } else if (country === 'se') {
      cardDataLocal = {
        name: 'BETALKORT',
      };
    } else if (country === 'de') {
      cardDataLocal = {
        name: 'ZAHLUNGSKARTE',
      };
    }
    const createCheckout = async () => {
      const AdyenCheckout = (await import('@adyen/adyen-web')).default;
      const googlePayConfiguration = {
        amount: {
          value: amount,
          currency,
        },
        countryCode: country.toUpperCase(),
        // Set this to PRODUCTION when you're ready to accept live payments
        environment: process.env.NEXT_PUBLIC_ENVIROMENT,
      };
      const applePayConfiguration = {
        amount: {
          value: amount,
          currency,
        },
        countryCode: country.toUpperCase(),
        environment: process.env.NEXT_PUBLIC_ENVIROMENT,
      };

      const paypalConfiguration = {
        environment: process.env.NEXT_PUBLIC_ENVIROMENT,
        countryCode: country.toUpperCase(),
        intent: 'capture',
        amount: {
          currency,
          value: amount,
        },
      };

      const configuration = {
        paymentMethodsResponse, // The `/paymentMethods` response from the server.
        clientKey: process.env.NEXT_PUBLIC_CLIENT_KEY, // Web Drop-in versions before 3.10.1 use originKey instead of clientKey.
        locale: localeString,
        translations,

        openFirstPaymentMethod: true,
        environment: process.env.NEXT_PUBLIC_ENVIROMENT,
        countryCode: country.toUpperCase(),
        amount: {
          currency,
          value: amount,
        },

        // Global configuration for onSubmit
        // Your function calling your server to make the `/payments` request
        onSubmit: async (state, dropin) => {
          // Pay button loading
          dropin.setStatus('loading');
          const d = new Date();
          const dateTimeOffset = d.getTimezoneOffset();

          const requestData = {
            session: sessionId,
            card_data: state.data.paymentMethod,
            membershipsid:
              membershipIds.length > 0 ? membershipIds : campaignMembershipIds,
            isIframe: isDunning,
            ...(terminalSession !== 'null' && {
              terminalSession,
            }),
            ...(serviceUnitUid && {
              serviceUnitUid,
            }),
            ...(skipMembershipValidation && {
              skip_mebership_validation: skipMembershipValidation,
              campaign_id: campaignId,
            }),
            browserInfo: {
              userAgent: navigator.userAgent,
              acceptHeader: meta?.request.headers.get('Accept'),
              language: navigator.language,
              colorDepth: window.screen.colorDepth,
              screenHeight: window.screen.height,
              screenWidth: window.screen.width,
              timeZoneOffset: dateTimeOffset,
              javaEnabled: navigator.javaEnabled(),
            },
          };

          // Post Payment
          const res = await postPayment(requestData);

          // Check for errors in the response
          if (res.error) {
            const { error } = res;
            onError(true);

            let errorMessage = 'An unknown error occurred';
            let statusCode = error.status || 'Unknown';

            // Check if the error is nested within `data`
            const errorData = error.data || error;

            // Case 1 & Case 4: { http_code, response }
            if (errorData.http_code && errorData.response) {
              statusCode = errorData.http_code;
              errorMessage = errorData.response;

              // Case 2 & Case 3: { message }
            } else if (errorData.message) {
              errorMessage = errorData.message;
            }

            // Capture the error using Sentry
            const err = new Error(
              `Failed to make payment: ${errorMessage} with errorCode: ${statusCode}`
            );
            captureException(err);

            // If there is an error message, set the dropin status to error
            if (errorMessage) {
              dropin.setStatus('error', {
                message: `${t('failedCardAction')}\n\n\n${errorMessage}`,
              });
            }
          }

          // Set a timeout to reset the dropin status after 4 seconds
          setTimeout(() => {
            dropin.setStatus('ready');
          }, 10000);

          // Handle successful payment data
          if (res.data) {
            onError(false);
            handlePaymentAccepted(res.data, dropin);
          }
        },
        paymentMethodsConfiguration: {
          card: {
            cardDataLocal,
            hasHolderName: true,
            holderNameRequired: true,
          },
          googlepay: googlePayConfiguration,
          applepay: applePayConfiguration,
          paypal: paypalConfiguration,
        },
        onError: (_error) => {
          console.error('_error', _error);

          setError('something went wrong!');
        },
        // Paypal
        onAdditionalDetails: async (state, dropin) => {
          const d = new Date();
          const dateTimeOffset = d.getTimezoneOffset();

          const reqBody = {
            ...state,
            session: sessionId,
            membershipsid: membershipIds,
            isIframe: isDunning,
            ...(terminalSession !== 'null' && {
              terminalSession,
            }),
            ...(campaignId && {
              campaign_id: campaignId,
            }),
            browserInfo: {
              userAgent: navigator.userAgent,
              acceptHeader: meta?.request.headers.get('Accept'),
              language: navigator.language,
              colorDepth: window.screen.colorDepth,
              screenHeight: window.screen.height,
              screenWidth: window.screen.width,
              timeZoneOffset: dateTimeOffset,
              javaEnabled: navigator.javaEnabled(),
            },
          };

          const res = await checkPayment(reqBody);

          if (res.error) {
            const { error } = res;

            let errorMessage = 'An unknown error occurred';
            let statusCode = error.status || 'Unknown';

            // Check if the error is nested within `data`
            const errorData = error.data || error;

            // Case 1 & Case 4: { http_code, response }
            if (errorData.http_code && errorData.response) {
              statusCode = errorData.http_code;
              errorMessage = errorData.response;

              // Case 2 & Case 3: { message }
            } else if (errorData.message) {
              errorMessage = errorData.message;
            }

            const err = new Error(
              `Failed to check payment: ${errorMessage} with errorCode: ${statusCode}`
            );
            captureException(err);

            dropin.setStatus('error', {
              message: `${t('failedCardAction')}\n\n\n${errorMessage}`,
            });

            setTimeout(() => {
              dropin.setStatus('ready');
            }, 10000);
          }

          if (res.data) {
            handlePaymentAccepted(res.data, dropin);
          }
        },
        // eslint-disable-next-line no-unused-vars
        onPaymentCompleted: (result, _dropin) => {
          // eslint-disable-next-line no-console
          console.log(result);
        },
      };

      const checkout = new AdyenCheckout(configuration);

      checkout.create('dropin').mount('#dropin-container');
    };

    createCheckout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethodsResponse, sessionId]);

  return (
    <>
      {error && (
        <Snackbar
          open={isSnackbarOpen}
          onClose={() => {
            setisSnackbarOpen(false);
            setError(null);
          }}
          autoHideDuration={null}
          anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
          TransitionComponent={Slide}
        >
          <Alert
            onClose={handleAlertClose}
            severity="error"
            sx={{ width: '100%', borderRadius: 0 }}
          >
            {t('failedCardAction')}
          </Alert>
        </Snackbar>
      )}
      <div id="dropin-container" />
    </>
  );
};

export default AdyenDropin;
